import { env } from '@/env';
import type {
  TrackType,
  ACCESSAlignmentResultCode,
  ACCESSEligibilityResultCode,
  EligibilityTrackStatus,
  CmsGender,
  AlignmentOutcomeCategory,
} from '@/types/registration';
import {
  isPilotMock,
  mockPostEligibility,
  mockGetEligibilityResult,
  mockPostAlignment,
  mockGetAlignmentResult,
} from '@/services/pilotMock';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

const ELIGIBILITY_URL = '/api/v1/eligibility';
const ALIGNMENT_URL = '/api/v1/alignment';

export interface EligibilitySubmitResponse {
  submissionId: string;
}

export type AdministrativeGender = CmsGender;

export interface EligibilityPatientName {
  family: string;
  given: string[];
}

// Matches the CMS Integration Service POST /api/v1/eligibility body
// (required: mbi, track, patientName, gender, birthDate).
export interface EligibilityRequest {
  mbi: string;
  track: TrackType;
  patientName: EligibilityPatientName;
  gender: AdministrativeGender;
  birthDate: string; // ISO date, YYYY-MM-DD
  // The promo code this registration came in on (?promo=). Optional, and never reaches CMS: the
  // service uses it as the second condition on per-MBI sandbox routing (CMS_SANDBOX_PROMO_CODES),
  // so a named test MBI only reaches the CMS sandbox when it arrives on a test promo code. Absent
  // is a valid value and means "no promo code" — ?invite=true skips the promo lookup entirely.
  promoCode?: string;
}

export interface EligibilityResultResponse {
  status: EligibilityTrackStatus | string;
  resultCode?: ACCESSEligibilityResultCode;
}

export interface AlignmentCondition {
  code: string;
  display: string;
  onsetDateTime: string;
}

export interface AlignmentRequest {
  track: TrackType;
  mbi: string;
  patientName: { family: string; given: string[] };
  gender: 'male' | 'female' | 'other' | 'unknown';
  birthDate: string;
  conditions: AlignmentCondition[];
  consentVoluntary: boolean;
  consentApiDataSharing: boolean;
  consentPcpSharing: boolean;
  consentControlGroup: boolean;
  consentTimestamp: number;
  // See EligibilityRequest.promoCode.
  promoCode?: string;
}

export type AlignmentSwitchRequest = AlignmentRequest & { switchConsentAttestation: true };

export interface AlignmentSubmitResponse {
  submissionId: string;
}

// GET /api/v1/alignment/:submissionId. The CMS Integration Service returns
// 202 { status } while pending/processing, and 200 { submissionId, status:'complete',
// resultCode, outcome } once terminal. 404/422 surface as CmsApiError (see unwrap).
export interface AlignmentResultResponse {
  submissionId?: string;
  // Closed union (no `| string`): the alignment route only ever emits these
  // statuses, so the poller's status handling stays exhaustive at compile time.
  status: EligibilityTrackStatus;
  resultCode?: ACCESSAlignmentResultCode;
  outcome?: AlignmentOutcomeCategory;
}

export class CmsApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'CmsApiError';
    this.status = status;
  }
}

const requestConfig = (
  authToken?: string,
  recaptchaToken?: string,
): AxiosRequestConfig | undefined => {
  const headers: Record<string, string> = {};
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  if (recaptchaToken) headers['X-Recaptcha-Token'] = recaptchaToken;
  return Object.keys(headers).length > 0 ? { headers } : undefined;
};

/**
 * Client for the CMS Integration Service (DACI 2). Kept separate from the
 * hello-server ApiClient in api.ts because it targets a different base URL.
 *
 * NOTE (RND-33880): eligibility runs pre-account-creation, so there is no user
 * auth token yet. `authToken` is optional here; the pre-auth/registration token
 * is resolved when the call is wired in RND-33880.
 */
export class CmsApiClient {
  private axiosInstance: AxiosInstance;

  constructor(baseURL: string) {
    this.axiosInstance = axios.create({
      baseURL,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  // Unwrap the response data, but never let a raw AxiosError escape: its
  // config/request/response carry the request payload (MBI, name, DOB, gender =
  // PHI). Surface only a sanitized message + status via CmsApiError so a caller
  // that logs the error cannot leak PHI into logs/analytics.
  private async unwrap<T>(call: Promise<AxiosResponse<T>>): Promise<T> {
    try {
      const response = await call;
      return response.data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        throw new CmsApiError(err.message, err.response?.status);
      }
      throw err;
    }
  }

  async postEligibility(
    input: EligibilityRequest,
    authToken?: string,
    recaptchaToken?: string,
  ): Promise<EligibilitySubmitResponse> {
    if (isPilotMock()) return mockPostEligibility();
    return this.unwrap(
      this.axiosInstance.post<EligibilitySubmitResponse>(
        ELIGIBILITY_URL,
        input,
        requestConfig(authToken, recaptchaToken),
      ),
    );
  }

  async getEligibilityResult(
    submissionId: string,
    authToken?: string,
  ): Promise<EligibilityResultResponse> {
    if (isPilotMock()) return mockGetEligibilityResult();
    return this.unwrap(
      this.axiosInstance.get<EligibilityResultResponse>(
        `${ELIGIBILITY_URL}/${submissionId}`,
        requestConfig(authToken),
      ),
    );
  }

  // Defined for completeness (WR-3). Exercised by RND-33882/RND-33883.
  async postAlignment(
    payload: AlignmentRequest,
    authToken?: string,
    recaptchaToken?: string,
  ): Promise<AlignmentSubmitResponse> {
    if (isPilotMock()) return mockPostAlignment();
    return this.unwrap(
      this.axiosInstance.post<AlignmentSubmitResponse>(
        ALIGNMENT_URL,
        payload,
        requestConfig(authToken, recaptchaToken),
      ),
    );
  }

  async postAlignmentSwitch(
    payload: AlignmentSwitchRequest,
    authToken?: string,
    recaptchaToken?: string,
  ): Promise<AlignmentSubmitResponse> {
    if (isPilotMock()) return mockPostAlignment();
    return this.unwrap(
      this.axiosInstance.post<AlignmentSubmitResponse>(
        `${ALIGNMENT_URL}/switch`,
        payload,
        requestConfig(authToken, recaptchaToken),
      ),
    );
  }

  // Poll the result of an alignment submission (RND-36355). 202 resolves with
  // { status: 'pending' | 'processing' }; 200 resolves with the terminal result
  // (status 'complete' + outcome). 404/422 reject as CmsApiError carrying the
  // HTTP status so the poller can distinguish not-found / failed-or-timed-out.
  async getAlignmentResult(
    submissionId: string,
    authToken?: string,
  ): Promise<AlignmentResultResponse> {
    if (isPilotMock()) return mockGetAlignmentResult(submissionId);
    return this.unwrap(
      this.axiosInstance.get<AlignmentResultResponse>(
        `${ALIGNMENT_URL}/${submissionId}`,
        requestConfig(authToken),
      ),
    );
  }
}

const CMS_PATH = env.IS_DEV ? '' : 'cms'; // staging/prod: CMS is path-routed behind the heartsapi gateway (mirrors hello-server /hs)
const CMS_BASE_URL = CMS_PATH ? `${env.CMS_SERVICE_URL}/${CMS_PATH}` : env.CMS_SERVICE_URL;
export const cmsApiClient = new CmsApiClient(CMS_BASE_URL);
